import { Identifier, XYCoord } from 'dnd-core'
import { useRef } from 'react'
import { useDrag, useDrop } from 'react-dnd'

export type DragItem = {
  index: number
  originalIndex: number
  id: string
  type: string
}

type UseVerticalDnDProps = {
  index: number
  accept: string
  onMove: (prevIndex: number, nextIndex: number) => void
  disabled?: boolean
}

/**
 * hook for dnd vertical
 * @param index
 * @param accept
 * @param onMove
 * @param disabled
 */
export const useVerticalDnD = ({
  index,
  accept,
  onMove,
  disabled = false,
}: UseVerticalDnDProps) => {
  const ref = useRef<HTMLTableRowElement>(null)

  const [{ handlerId }, drop] = useDrop<
    DragItem,
    void,
    { handlerId: Identifier | null }
  >({
    accept,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      }
    },
    hover(item: DragItem, monitor) {
      if (!ref.current || disabled) {
        return
      }
      const dragIndex = item.index
      const hoverIndex = index

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect()

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2

      // Determine mouse position
      const clientOffset = monitor.getClientOffset()

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex
    },
    drop(item: DragItem & { originalIndex: number }) {
      // Only call onMove when actually dropping after dragging (for API calls)
      if (!disabled && item.originalIndex !== index) {
        onMove(item.originalIndex, index)
      }
    },
  })

  const [{ opacity }, drag, preview] = useDrag({
    type: accept,
    item: () => {
      return { index, originalIndex: index }
    },
    collect: monitor => ({
      opacity: monitor.isDragging() ? 0.4 : 1,
    }),
    canDrag: !disabled,
  })

  preview(drop(ref))

  return { ref, drag, opacity }
}
